import PropTypes from 'prop-types';
import { Col, Row, Table, Typography } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { sentenceCase } from 'change-case';
import { sortableColumnTitle } from 'src/pages/concierge/ProposalBuilder/columns/usePbColumns';
import { locationRender } from 'src/pages/rfq/proposal/supply/SupplyRoute';
import { Box } from 'src/components/project/explore/summary/helpers';
import { MetaPropertyName } from 'src/hooks/useMetaProperty';
import { PropertyCompare } from 'src/components/project/explore/supplier-list/BalesTdsRow';
import {
  KpiRangeCompare,
  kpiMid,
  PriceCompare
} from 'src/components/project/explore/supplier-list/TdsRow';
import { currencyValuePrice } from 'src/utils/currency';
import { prettyNumberRound } from 'src/components/utils/prettyNumber';
import numericRange from 'src/components/utils/numericRange';
import { capacityValueToLabel } from 'src/components/project/explore/filters/CapacityFilter';
import { LogPricingModalTrigger } from 'src/components/supplier/LogPricingModal';
import { primaryKpisByMaterial } from 'src/components/project/hooks/usePrimaryKpis';
import useColumnManager from 'src/components/project/source/useColumnManager';
import { KpiName } from 'src/components/project/explore/filters/KpiFilter';
import falsyNot0 from 'src/components/utils/falsyNot0';
import { EditableFunnelStageTag } from 'src/components/project/source/FunnelStageTag';
import ChatIcon from 'src/components/icons/Chat';
import { useIntercom } from 'react-use-intercom';
import { FormOutlined } from '@ant-design/icons';

export default function TdsTable({
  tdss,
  project,
  isLoading,
  localSort,
  pagination
}) {
  const { TableComponent } = useTdsTable({
    tdss,
    project,
    isLoading,
    localSort,
    pagination
  });

  return TableComponent;
}
TdsTable.propTypes = {
  tdss: PropTypes.array,
  isLoading: PropTypes.bool,
  localSort: PropTypes.bool,
  project: PropTypes.object,
  pagination: PropTypes.object
};

const EMPTY_PROJECT = {};

export function useTdsTable({
  tdss,
  project = EMPTY_PROJECT,
  isLoading,
  pagination,
  localSort
}) {
  const [selectedRows, setSelectedRows] = useState([]);

  const { Checkboxes: ColumnsManager, updatedColumns } = useTdsTableColumns(
    tdss,
    project,
    localSort
  );

  useEffect(() => {
    setSelectedRows([]);
  }, [tdss]);

  const rfqRow = useMemo(
    () => (
      <Table.Summary fixed="top">
        <Table.Summary.Row>
          {/* Placeholder for checkbox column */}
          <Table.Summary.Cell index={0}></Table.Summary.Cell>
          {updatedColumns.map((c, i) => {
            let renderedValue = null;
            try {
              const value = Array.isArray(c.dataIndex)
                ? c.dataIndex.reduce(
                    (currentValue, currentIndex) => currentValue[currentIndex],
                    project
                  )
                : project[c.dataIndex];

              renderedValue = value;
              if (c.rfqRender) renderedValue = c.rfqRender(value, project);
              else if (c.render) renderedValue = c.render(value, project);
            } catch (_) {
              renderedValue = '-';
            }
            return (
              <Table.Summary.Cell
                key={
                  c.key ||
                  (Array.isArray(c.dataIndex)
                    ? c.dataIndex.join('.')
                    : c.dataIndex)
                }
                index={i + 1}
                colSpan={c.hidden ? 0 : 1}
              >
                <Typography.Text strong>{renderedValue}</Typography.Text>
              </Table.Summary.Cell>
            );
          })}
        </Table.Summary.Row>
      </Table.Summary>
    ),
    [updatedColumns]
  );

  const TableComponent = (
    <Table
      className="search-results--table tds-list"
      rowKey="id"
      loading={isLoading}
      summary={!project ? undefined : () => rfqRow}
      columns={updatedColumns}
      dataSource={tdss || []}
      rowSelection={{
        type: 'checkbox',
        onChange: (_, newSelectedRows) => setSelectedRows(newSelectedRows),
        preserveSelectedRowKeys: true,
        selectedRowKeys: selectedRows.map(({ id }) => id)
      }}
      scroll={{
        scrollToFirstRowOnChange: true,
        x: 'scroll'
      }}
      sticky
      pagination={pagination}
    />
  );

  return {
    TableComponent,
    ColumnsManager
  };
}

function totalCost(tdsObj) {
  return (
    (tdsObj.project_tariff || 0) +
      (tdsObj.project_fee || 0) +
      (tdsObj.project_freight || 0) +
      (tdsObj.price?.price || 0) || null
  );
}

function totalCostTarget(tdsObj) {
  return (
    (tdsObj.target_tariff || 0) +
      (tdsObj.target_fee || 0) +
      (tdsObj.target_freight || 0) +
      (tdsObj.price || 0) || null
  );
}

function useTdsTableColumns(tdss, project, localSort) {
  const firstTds = tdss?.[0];

  const { filters } = project;

  const kpiColumns = useMemo(() => {
    const kpiCodes = new Set(
      firstTds
        ? Object.keys(firstTds)
            .filter((tdsAttr) => tdsAttr.startsWith('kpi_'))
            .map((tdsAttr) => {
              const match = tdsAttr.match(/kpi_(.+?)\./);
              return match[1];
            })
        : []
    );
    const primaryKpis =
      (firstTds && primaryKpisByMaterial[firstTds.type_code]) ||
      primaryKpisByMaterial.DEFAULT;

    return [...kpiCodes]
      .sort((a) => (primaryKpis.includes(a) ? -1 : 0))
      .map((kpiCode) => {
        const kpiKey = `kpi_${kpiCode}`;

        return {
          title: sortableColumnTitle(
            <KpiName codeOrUuid={kpiCode} />,
            `${kpiKey}`
          ),
          sorter:
            !localSort || ((a, b) => kpiMid(kpiCode, a) - kpiMid(kpiCode, b)),
          dataIndex: `${kpiKey}.code`,
          sortKey: `${kpiKey}`,
          key: kpiCode,
          width: 120,
          render: (_, tdsObj) => {
            const kpiFilter = filters?.kpis?.[kpiCode];

            return (
              <KpiRangeCompare
                kpiCode={kpiCode}
                tds={tdsObj}
                kpiFilter={kpiFilter}
              />
            );
          },
          rfqRender: (
            _,
            {
              filters: {
                kpis: {
                  [kpiCode]: { min, max, units }
                }
              }
            }
          ) =>
            numericRange({
              min,
              max,
              empty: '--',
              rangeRender: (rangeMin, rangeMax) =>
                `${rangeMin} - ${rangeMax} ${units || ''}`,
              noMaxRender: (v) => `> ${v} ${units || ''}`,
              noMinRender: (v) => `< ${v} ${units || ''}`,
              singleValueRender: (v) => `${v} ${units || ''}`
            })
        };
      });
  }, [tdss]);

  const propertyColumns = useMemo(() => {
    const propertyCodes = new Set(
      firstTds
        ? Object.keys(firstTds)
            .filter((tdsAttr) => tdsAttr.startsWith('property_'))
            .map((tdsAttr) => {
              const match = tdsAttr.match(/^property_(.*)_[^_]+$/);
              return match[1];
            })
        : []
    );

    return [...propertyCodes].map((propertyCode) => {
      const propertyKey = `property_${propertyCode}`;
      const propertyFilter = filters?.properties?.[propertyCode];

      return {
        title: <MetaPropertyName property={propertyCode} />,
        sorter:
          !localSort ||
          ((a, b) =>
            a[`property_${propertyCode}_code`]
              .join('')
              .localeCompare(b[`property_${propertyCode}_code`].join(''))),
        dataIndex: `${propertyKey}.code`,
        sortKey: `${propertyKey}`,
        key: propertyCode,
        width: 120,
        render: (_, tdsObj) => (
          <PropertyCompare
            tds={tdsObj}
            propertyCode={propertyCode}
            propertyFilter={propertyFilter}
          />
        ),
        rfqRender: (
          _,
          {
            filters: {
              properties: { [propertyCode]: propertyValues }
            }
          }
        ) =>
          propertyValues.map((value, i) => (
            <React.Fragment key={value}>
              <MetaPropertyName property={propertyCode} optionValue={value} />
              {i + 1 < propertyValues.length ? ', ' : ''}
            </React.Fragment>
          ))
      };
    });
  }, [tdss]);

  const priceColumns = useMemo(
    () => [
      {
        title: sortableColumnTitle('Material Price', `price.price`),
        dataIndex: `price.price`,
        sorter: (a, b) => (a.price?.price || 0) - (b.price?.price || 0),
        sortKey: 'price',
        key: 'price',
        width: 120,
        render: (_, tdsObj) => (
          <LogPricingModalTrigger
            tdsLocationId={tdsObj.project_tds_location_id}
          >
            <PriceCompare
              value={tdsObj?.price?.price}
              target={project.price}
              currency={project.currency}
              units={project.price_weight_units}
            />
          </LogPricingModalTrigger>
        ),
        rfqRender: (_, { price, currency, price_weight_units }) =>
          falsyNot0(price)
            ? '--'
            : `${currencyValuePrice(price, currency, price_weight_units)}`
      }
      // {
      //   title: 'Terms',
      //   sorter: true,
      //   dataIndex: `price.terms`,
      //   sortKey: 'terms',
      //   key: 'terms',
      //   width: 120,
      //   render: (_, tdsObj) => (
      //     <ValueCompare label={tdsObj?.price?.terms} type="neutral" />
      //   ),
      //   rfqRender: () => ''
      // }
    ],
    [tdss, project]
  );

  const statusList = useMemo(
    () => [
      ...new Set(((localSort && tdss) || []).flatMap((t) => t.project_status))
    ],
    [tdss]
  );

  const { showConversation, show } = useIntercom();
  const projectColumns = useMemo(
    () => [
      {
        title: sortableColumnTitle('Freight', `project_freight`),
        dataIndex: `project_freight`,
        sorter:
          !localSort ||
          ((a, b) => (a.project_freight || 0) - (b.project_freight || 0)),
        key: 'project_freight',
        width: 120,
        render: (_, tdsObj) => (
          <PriceCompare
            value={tdsObj?.project_freight}
            target={project.target_freight}
            currency={project.currency}
            units={project.price_weight_units}
          />
        ),
        rfqRender: (_, { target_freight, currency, price_weight_units }) =>
          falsyNot0(target_freight)
            ? '--'
            : `${currencyValuePrice(
                target_freight,
                currency,
                price_weight_units
              )}`
      },
      {
        title: sortableColumnTitle('Tariffs', 'project_tariff'),
        dataIndex: `project_tariff`,
        sorter:
          !localSort ||
          ((a, b) => (a.project_tariff || 0) - (b.project_tariff || 0)),
        key: 'project_tariff',
        width: 120,
        render: (_, tdsObj) => (
          <PriceCompare
            value={tdsObj?.project_tariff}
            target={project.target_tariff}
            currency={project.currency}
            units={project.price_weight_units}
          />
        ),
        rfqRender: (_, { target_tariff, currency, price_weight_units }) =>
          falsyNot0(target_tariff)
            ? '--'
            : `${currencyValuePrice(
                target_tariff,
                currency,
                price_weight_units
              )}`
      },
      // {
      //   title: sortableColumnTitle('Fees', 'project_fee'),
      //   dataIndex: `project_fee`,
      //   sorter:
      //     !localSort || ((a, b) => (a.project_fee || 0) - (b.project_fee || 0)),
      //   key: 'project_fee',
      //   width: 120,
      //   render: (_, tdsObj) => (
      //     <PriceCompare
      //       value={tdsObj?.project_fee}
      //       target={project.target_fee}
      //       currency={project.currency}
      //       units={project.price_weight_units}
      //     />
      //   ),
      //   rfqRender: (_, { target_fee, currency, price_weight_units }) =>
      //     `${currencyValuePrice(target_fee, currency, price_weight_units)}`
      // },
      {
        title: sortableColumnTitle('Total cost', 'total_cost'),
        dataIndex: `total_cost`,
        sortKey: 'total_cost',
        sorter: !localSort || ((a, b) => totalCost(a) - totalCost(b)),
        key: 'total_cost',
        width: 120,
        render: (_, tdsObj) => (
          <PriceCompare
            value={totalCost(tdsObj)}
            target={totalCostTarget(project)}
            currency={project.currency}
            units={project.price_weight_units}
          />
        ),
        rfqRender: (_, { currency, price_weight_units, ...rfq }) =>
          !totalCostTarget(rfq)
            ? '--'
            : `${currencyValuePrice(
                totalCostTarget(rfq),
                currency,
                price_weight_units
              )}`
      }
      // {
      //   title: 'Status',
      //   sorter: !localSort || ((a, b) => totalCost(a) - totalCost(b)),
      //   filters:
      //     localSort &&
      //     statusList.map((c) => ({
      //       text: c,
      //       value: c
      //     })),
      //   dataIndex: `project_status`,
      //   sortKey: 'project_status',
      //   key: 'project_status',
      //   width: 120,
      //   render: (_, tdsObj) => (
      //     <ValueCompare label={tdsObj.project_status} type="neutral" />
      //   ),
      //   rfqRender: () => ''
      // }
    ],
    [tdss]
  );

  const countryList = useMemo(
    () => [...new Set(((localSort && tdss) || []).flatMap((t) => t.country))],
    [tdss]
  );

  const funnelStageList = useMemo(
    () => [
      ...new Set(
        ((localSort && tdss) || []).flatMap((t) => t.project_funnel_stage)
      )
    ],
    [tdss]
  );
  const allColumns = useMemo(
    () => [
      {
        title: sortableColumnTitle('Supplier name', 'company_name'),
        sorter:
          !localSort ||
          ((a, b) => (a.project_order || 0) - (b.project_order || 0)),
        filters:
          localSort &&
          countryList.map((c) => ({
            text: c,
            value: c
          })),
        onFilter: (value, record) => record.country.some((a) => a === value),
        sortKey: 'company_name',
        dataIndex: 'company_name',
        fixed: 'left',
        width: 320,
        render: (companyName, tdsObj) => (
          <Row
            justify="space-between"
            align="middle"
            wrap={false}
            gutter={[16, 16]}
          >
            <Col>
              <span className="company">
                <a
                  href={`/supplier/${tdsObj.company_id}`}
                  title={`${tdsObj.company_name} profile`}
                  target="_blank"
                >
                  {companyName}
                </a>
                {tdsObj.sku ? (
                  <>
                    {' '}
                    (<Typography.Text italic>{tdsObj.sku}</Typography.Text>)
                  </>
                ) : null}
              </span>
              <span className="subcontent">{locationRender(tdsObj)}</span>
            </Col>
          </Row>
        ),
        rfqRender: () => ''
      },
      ...(project
        ? [
            {
              title: sortableColumnTitle('Status', 'project_funnel_stage'),
              sorter:
                !localSort ||
                ((a, b) =>
                  (a.project_funnel_stage || 0) -
                  (b.project_funnel_stage || 0)),
              filters:
                localSort &&
                funnelStageList.map((c) => ({
                  text: sentenceCase(c || ''),
                  value: c
                })),
              onFilter: (value, record) =>
                record.project_funnel_stage === value,
              sortKey: 'project_funnel_stage',
              dataIndex: 'project_funnel_stage',
              fixed: 'left',
              width: 145,
              render: (projectFunnelStage, tdsObj) => (
                <Row gutter={[8, 8]}>
                  <Col flex="1">
                    <EditableFunnelStageTag
                      projectTdsId={tdsObj.project_tds_id}
                      stage={tdsObj.project_funnel_stage}
                    />
                  </Col>
                  <Col>
                    <button
                      className="bare"
                      type="button"
                      onClick={() =>
                        (tdsObj.project_intercom_conversation_id &&
                          showConversation(
                            tdsObj.project_intercom_conversation_id
                          )) ||
                        show()
                      }
                    >
                      <ChatIcon />
                    </button>
                  </Col>
                  <Col>
                    <button className="bare" type="button" onClick={() => {}}>
                      <FormOutlined />
                    </button>
                  </Col>
                </Row>
              ),
              rfqRender: () => ''
            }
          ]
        : []),
      {
        title: sortableColumnTitle('Type', 'type'),
        sorter: true,
        sortKey: 'type_code',
        width: 120,
        dataIndex: 'type',
        render: (val) => <Box>{val}</Box>,
        rfqRender: (_, { type_code__in }) => (
          <MetaPropertyName property="type" optionValue={type_code__in} />
        )
      },
      {
        title: sortableColumnTitle('Form', 'form'),
        sorter: true,
        sortKey: 'form_code',
        width: 120,
        dataIndex: 'form',
        render: (val) => <Box>{val}</Box>,
        rfqRender: (_, { form_code__in }) => (
          <MetaPropertyName property="form" optionValue={form_code__in} />
        )
      },
      {
        title: sortableColumnTitle('Quantity', 'capacity'),
        sorter:
          !localSort ||
          ((a, b) =>
            (a.capacity || a.supply_capacity || 0) -
            (b.capacity || b.supply_capacity || 0)),
        dataIndex: 'capacity',
        width: 120,
        render: (cap, tdsObj) => (
          <Box>
            {prettyNumberRound(cap || tdsObj.supply_capacity)}{' '}
            {tdsObj.capacity_units}
          </Box>
        ),
        rfqRender: (_, { filters: { capacity__gte } }) =>
          capacityValueToLabel(capacity__gte)
      },
      ...priceColumns,
      ...projectColumns,
      ...kpiColumns,
      ...propertyColumns
    ],
    [kpiColumns, propertyColumns, priceColumns, project]
  );

  return useColumnManager(allColumns);
}
